import { useAccountId, FileUploadWithLoader } from '@labrav/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  LabraToolTipV2,
  RadioButtonGroupMapping,
  RadioButtonType,
  RadioButtonsV2,
} from '@labrav/react-components'
import React, { useState } from 'react'
import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons'
import { FormikProps, useFormikContext } from 'formik'
import { EulaType } from '../../../types/privateOffer'
import { Typography } from '@mui/material'
import { useStyles } from './FlyOutProductEula.styles'
import { isEmpty } from 'lodash'
import { DefaultNowType } from '../../../common/utils/dateTimeNow'
import {
  bucketName,
  eulaStandardContractUrl,
} from '../../../common/utils/constants'
import { RequirementLink } from '../../../types/RequirementLink'
import { FormLockedType } from '../../../types/FormLockedType'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import {
  eulaFileUploadDescription,
  fileFormatIncorrectText,
  eulaFileUploadButtonText,
  eulaFileUploadContainerTitle,
} from '../../../common/utils/productCreationUtil'
import { FlyoutProductDetailsEulaFaqs } from './FlyoutProductDetailsEulaFaqs'

export type FlyOutProductEulaProps = {
  now: DefaultNowType
  handleRefLinkClick?: (inex: number) => void
  detailView?: boolean
  detailViewTooltip?: string
} & RequirementLink &
  FormLockedType

export const FlyOutProductEula: React.FC<FlyOutProductEulaProps> = ({
  now,
  isFormLocked,
  handleRefLinkClick,
  detailView,
  detailViewTooltip,
}) => {
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const { values, errors, touched, setFieldValue } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const { classes } = useStyles()
  const generalInformation = values.generalInformation
  const generalInformationErrors = errors.generalInformation
  const generalInformationTouched = touched.generalInformation

  const isReadOnlyValue = !!isFormLocked || !!detailView

  const handleEulaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value as string
    setFieldValue('generalInformation.eula', value)
  }
  const updateEulaUrl = (fileUrl: string) => {
    setFieldValue('generalInformation.eulaUrl', fileUrl)
  }
  const deleteEulaUrl = () => {
    setFieldValue('generalInformation.eulaUrl', '')
  }
  const [indexModal, setIndexModal] = useState<number>(1)
  const eulaButtonOptionsV2: RadioButtonGroupMapping[] = [
    {
      key: EulaType.STANDARD,
      label: 'Standard contract for AWS marketplace (SCMP)',
      type: RadioButtonType.STANDARD,
      url: eulaStandardContractUrl,
      infoDescription:
        'SCMP is a standardized contract template intended to help streamline procurement and speed up transactions.',
      disabled: isReadOnlyValue,
    },
    {
      key: EulaType.CUSTOM,
      label: 'Custom EULA',
      type: RadioButtonType.STANDARD,
      infoDescription:
        'Provide a file(.pdf / .txt) to a custom end-user license agreement (EULA) for your product.',
      disabled: isReadOnlyValue,
    },
  ]
  return (
    <BoxV2
      heading={'Configure End-user license agreement (EULA)'}
      subHeading={
        <div>
          At the point of purchase, buyers must agree to the terms and
          conditions outlined in your product's end-user license agreement
          (EULA).{' '}
          <ButtonV2
            styleType="link"
            onClick={() => handleRefLinkClick && handleRefLinkClick(1)}
          >
            (Refer no. 7 in image no. 2)
          </ButtonV2>
        </div>
      }
      headingIcon={
        <FontAwesomeIcon
          icon={faBadgeCheck}
          style={{ width: '14px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductDetailsEulaFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <LabraToolTipV2
        tooltipPlacement="bottom-start"
        description={detailViewTooltip}
        followCursor={detailView}
      >
        <div className={classes.sectionContainer} data-testid="product-eula">
          <Typography className={classes.sectionHeading}>
            Select EULA configuration option
          </Typography>
          <RadioButtonsV2
            name={'generalInformation.eula'}
            checkedValue={{
              key: generalInformation?.eula,
              value: generalInformation?.eula,
            }}
            handleChange={handleEulaChange}
            radioButtonMappings={eulaButtonOptionsV2}
            gapBetween="16px"
            vertical
          />
        </div>
        {!!generalInformationTouched?.eula &&
          generalInformationErrors?.eula && (
            <div className={classes.errorMessage}>
              {generalInformationErrors?.eula}
            </div>
          )}
        {generalInformation?.eula === EulaType.CUSTOM ? (
          <div className={classes.sectionContainer} data-testid="file-uploader">
            <Typography className={classes.sectionHeading}>
              Custom EULA
            </Typography>
            <FileUploadWithLoader
              keyName={'eula-upload-product'}
              partnerId={accountId}
              existingFileLinks={
                isEmpty(generalInformation?.eulaUrl)
                  ? []
                  : [generalInformation?.eulaUrl]
              }
              assetType="eula_files"
              folder="product_eula_files"
              fileUploadDescription={eulaFileUploadDescription}
              quantity={1}
              fileUploadType="txt/pdf"
              unit="MB"
              uploadFileSize={5}
              acceptedFormats={['.pdf', '.txt']}
              updateUrl={updateEulaUrl}
              deleteUrl={deleteEulaUrl}
              defaultNow={now}
              fileUploadButtonText={eulaFileUploadButtonText}
              fileFormatIncorrectText={fileFormatIncorrectText}
              fileUploadContainerTitle={eulaFileUploadContainerTitle}
              bucketName={bucketName}
              uploadError={
                (!!generalInformationTouched?.eulaUrl &&
                  generalInformationErrors?.eulaUrl) ||
                ''
              }
              viewOnly={detailView}
              checkDuplicate
              showRealFileName
            />
          </div>
        ) : (
          <></>
        )}
      </LabraToolTipV2>
    </BoxV2>
  )
}
